import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Clock, RefreshCw, Upload, XCircle, CheckCircle2, ChevronDown, ChevronRight,
  Trash2, Save, Loader2, Globe, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { MarketFlag } from '../lib/MarketFlag';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  pending_review:     'Väntar granskning',
  published:          'Publicerad',
  partial_published:  'Delvis publicerad',
  publish_failed:     'Publicering misslyckades',
  rejected:           'Avvisad',
  needs_manual_input: 'Manuell input krävs',
  scraping_failed:    'Skrapning misslyckades',
  generation_failed:  'Generering misslyckades',
  save_failed:        'Spara-fel',
};

const FILTERS = [
  ['pending', 'Väntar granskning', ['pending_review', 'needs_manual_input', 'scraping_failed', 'generation_failed', 'bullets_failed', 'save_failed']],
  ['published', 'Publicerade', ['published', 'partial_published']],
  ['failed', 'Misslyckade', ['publish_failed']],
  ['rejected', 'Avvisade', ['rejected']],
  ['all', 'Alla', null],
];

function formatDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('sv-SE', { dateStyle: 'medium', timeStyle: 'short' });
}

async function consumeSse(response, onEvent) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop();
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      try { onEvent(JSON.parse(line.slice(6))); } catch (_) { /* ignore */ }
    }
  }
}

function MarketDetailBlock({ market, content, publishState, onChange }) {
  const desc = content?.description || '';
  const mt = content?.meta_title || '';
  const md = content?.meta_description || '';
  const hasError = content?.error;
  const status = publishState?.status; // 'loading' | 'ok' | 'fail' | undefined

  return (
    <div className="ap-queue-market-block" data-testid={`ap-queue-market-${market}`}>
      <div className="ap-queue-market-header">
        <span className="ap-queue-market-title">
          <MarketFlag market={market} /> {market}
        </span>
        {status === 'loading' && <Loader2 size={12} className="ap-progress-spin" />}
        {status === 'ok' && <span className="ap-mkt-chip ap-mkt-chip--ok">✓ publicerad</span>}
        {status === 'fail' && <span className="ap-mkt-chip ap-mkt-chip--fail" title={publishState.error}>✗ fel</span>}
        {hasError && !status && (
          <span className="ap-mkt-chip ap-mkt-chip--fail" title={content.error}>Generering misslyckades</span>
        )}
      </div>

      {!hasError && (
        <>
          <div className="ap-queue-field ap-queue-field--desc">
            <div className="ap-queue-field-label">
              <span>Produktbeskrivning</span>
              <span className="ap-queue-field-count">{desc.length} tecken</span>
            </div>
            <textarea
              value={desc}
              onChange={(e) => onChange(market, 'description', e.target.value)}
              data-testid={`ap-queue-desc-${market}`}
              placeholder="HTML-beskrivning..."
            />
          </div>
          <div className="ap-queue-field">
            <div className="ap-queue-field-label">
              <span>Meta Title</span>
              <span className="ap-queue-field-count">{mt.length}/60 tecken</span>
            </div>
            <input
              type="text"
              value={mt}
              onChange={(e) => onChange(market, 'meta_title', e.target.value)}
              data-testid={`ap-queue-mt-${market}`}
            />
          </div>
          <div className="ap-queue-field">
            <div className="ap-queue-field-label">
              <span>Meta Description</span>
              <span className="ap-queue-field-count">{md.length}/160 tecken</span>
            </div>
            <textarea
              value={md}
              onChange={(e) => onChange(market, 'meta_description', e.target.value)}
              data-testid={`ap-queue-md-${market}`}
              style={{ minHeight: 70 }}
            />
          </div>
        </>
      )}
    </div>
  );
}

function QueueRowDetail({ row, onSaved, onRemoved }) {
  const { user } = useAuth() || {};
  const [content, setContent] = useState(row.generated_content || {});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishState, setPublishState] = useState({}); // { [market]: {status, error} }

  // Re-sync if row changes externally (e.g. after publish)
  useEffect(() => { setContent(row.generated_content || {}); setDirty(false); }, [row.id, row.generated_content]);

  const onFieldChange = (market, field, value) => {
    setContent(prev => ({
      ...prev,
      [market]: { ...(prev[market] || {}), [field]: value },
    }));
    setDirty(true);
  };

  const saveEdits = async () => {
    if (!supabase || !dirty) return;
    setSaving(true);
    const { error } = await supabase
      .from('pipeline_queue')
      .update({ generated_content: content })
      .eq('id', row.id);
    setSaving(false);
    if (error) {
      toast.error(`Kunde inte spara: ${error.message}`);
    } else {
      setDirty(false);
      toast.success('Ändringar sparade');
      onSaved && onSaved({ ...row, generated_content: content });
    }
  };

  const reject = async () => {
    if (!supabase) return;
    if (!window.confirm('Avvisa denna produkt? Den kan återställas via "Avvisade"-filtret.')) return;
    const { error } = await supabase
      .from('pipeline_queue')
      .update({ status: 'rejected' })
      .eq('id', row.id);
    if (error) {
      toast.error(`Kunde inte avvisa: ${error.message}`);
    } else {
      toast.success('Produkt avvisad');
      onRemoved && onRemoved(row.id);
    }
  };

  const publishAll = async () => {
    if (!user || !supabase) return;
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;
    if (!accessToken) { toast.error('Ingen aktiv session'); return; }

    if (!row.part_no) {
      toast.error('PartNo saknas — produkten kan inte publiceras');
      return;
    }

    const markets = row.markets || Object.keys(content);
    const validMarkets = markets.filter(m => content[m] && !content[m].error);
    if (validMarkets.length === 0) {
      toast.error('Inga giltiga marknader att publicera');
      return;
    }

    // Persist any pending edits first
    if (dirty) {
      await supabase.from('pipeline_queue').update({ generated_content: content }).eq('id', row.id);
      setDirty(false);
    }

    setPublishing(true);
    const initial = {};
    validMarkets.forEach(m => { initial[m] = { status: 'loading' }; });
    setPublishState(initial);

    try {
      const resp = await fetch(`${API_URL}/api/autopilot/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          access_token: accessToken,
          queue_id: row.id,
          part_no: row.part_no,
          markets: validMarkets,
          generated_content: content,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: resp.status }));
        toast.error(`Publicering misslyckades: ${err.detail || resp.status}`);
        setPublishing(false);
        setPublishState({});
        return;
      }

      await consumeSse(resp, (ev) => {
        if (ev.type === 'market_result') {
          setPublishState(prev => ({
            ...prev,
            [ev.market]: {
              status: ev.success ? 'ok' : 'fail',
              error: ev.error,
            },
          }));
        } else if (ev.type === 'done') {
          const updated = {
            ...row,
            status: ev.status,
            published_markets: ev.published_markets,
            publish_errors: ev.errors,
            published_at: ev.published_markets?.length ? new Date().toISOString() : row.published_at,
          };
          onSaved && onSaved(updated);
          if (ev.status === 'published') toast.success('Publicerad till alla marknader!');
          else if (ev.status === 'partial_published') toast.warning(`Delvis publicerad (${ev.published_markets.length}/${ev.total_markets})`);
          else toast.error('Publicering misslyckades för alla marknader');
        } else if (ev.type === 'error') {
          toast.error(ev.message);
        }
      });
    } catch (e) {
      toast.error(`Anslutningsfel: ${e.message}`);
    } finally {
      setPublishing(false);
    }
  };

  const markets = row.markets || Object.keys(content);
  const isPublished = row.status === 'published';

  return (
    <div className="ap-queue-detail-inner" data-testid={`ap-queue-detail-${row.id}`}>
      {row.bullets && (
        <div className="ap-queue-field" style={{ marginBottom: 16 }}>
          <div className="ap-queue-field-label"><span>Bullets (från skrapning)</span></div>
          <div className="ap-queue-bullets-box">{row.bullets}</div>
          {(() => {
            // Collect unique source_urls from any market
            const urls = new Set();
            Object.values(content).forEach(c => (c?.source_urls || []).forEach(u => urls.add(u)));
            const arr = Array.from(urls).slice(0, 6);
            if (arr.length === 0) return null;
            return (
              <div className="ap-queue-sources">
                Källor:
                {arr.map((u, i) => (
                  <a key={i} href={u} target="_blank" rel="noopener noreferrer">
                    {(() => { try { return new URL(u).hostname; } catch { return u.slice(0, 40); } })()}
                  </a>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {markets.map(m => (
        <MarketDetailBlock
          key={m}
          market={m}
          content={content[m] || {}}
          publishState={publishState[m]}
          onChange={onFieldChange}
        />
      ))}

      <div className="ap-queue-detail-actions">
        <button
          className="ap-btn-pill"
          onClick={saveEdits}
          disabled={!dirty || saving}
          data-testid={`ap-queue-save-${row.id}`}
        >
          {saving ? <Loader2 size={12} className="ap-progress-spin" /> : <Save size={12} />}
          {dirty ? 'Spara ändringar' : 'Inga ändringar'}
        </button>
        <button
          className="ap-btn-pill ap-btn-pill--primary"
          onClick={publishAll}
          disabled={publishing || isPublished}
          data-testid={`ap-queue-publish-${row.id}`}
        >
          {publishing
            ? <><Loader2 size={12} className="ap-progress-spin" /> Publicerar...</>
            : isPublished
              ? <><CheckCircle2 size={12} /> Publicerad</>
              : <><Upload size={12} /> Publicera till Norce</>}
        </button>
        {!isPublished && row.status !== 'rejected' && (
          <button
            className="ap-btn-pill ap-btn-pill--danger"
            onClick={reject}
            data-testid={`ap-queue-reject-${row.id}`}
          >
            <Trash2 size={12} /> Avvisa
          </button>
        )}
      </div>
    </div>
  );
}

export default function QueueTab() {
  const { user } = useAuth() || {};
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('pending');
  const [expandedId, setExpandedId] = useState(null);
  const [selected, setSelected] = useState({});
  const [bulkPublishing, setBulkPublishing] = useState(false);
  const [clearModal, setClearModal] = useState(false);
  const [clearing, setClearing] = useState(false);

  const loadRows = useCallback(async () => {
    if (!user || !supabase) { setLoading(false); return; }
    setLoading(true);
    const { data, error } = await supabase
      .from('pipeline_queue')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(500);
    setLoading(false);
    if (error) {
      toast.error(`Kunde inte hämta kö: ${error.message}`);
      return;
    }
    setRows(data || []);
  }, [user]);

  useEffect(() => { loadRows(); }, [loadRows]);

  // Counts per filter bucket
  const counts = useMemo(() => {
    const c = {};
    FILTERS.forEach(([key, , statuses]) => {
      c[key] = statuses
        ? rows.filter(r => statuses.includes(r.status)).length
        : rows.length;
    });
    return c;
  }, [rows]);

  const filteredRows = useMemo(() => {
    const f = FILTERS.find(x => x[0] === activeFilter);
    if (!f || !f[2]) return rows;
    return rows.filter(r => f[2].includes(r.status));
  }, [rows, activeFilter]);

  const selectedIds = Object.keys(selected).filter(id => selected[id]);
  const selectablePublishable = filteredRows.filter(r => r.status === 'pending_review' || r.status === 'publish_failed' || r.status === 'partial_published');
  const allSelected = selectablePublishable.length > 0 && selectablePublishable.every(r => selected[r.id]);

  const toggleAll = () => {
    if (allSelected) {
      setSelected({});
    } else {
      const next = {};
      selectablePublishable.forEach(r => { next[r.id] = true; });
      setSelected(next);
    }
  };

  const handleRowUpdate = (updated) => {
    setRows(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r));
  };

  const handleRowRemove = (id) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, status: 'rejected' } : r));
    if (expandedId === id) setExpandedId(null);
  };

  // Quick reject from row-level button (no expand needed)
  const handleQuickReject = async (e, row) => {
    e.stopPropagation();
    if (!supabase) return;
    if (!window.confirm(`Avvisa "${row.product_name}"? Den kan återställas via "Avvisade"-filtret.`)) return;
    const { error } = await supabase
      .from('pipeline_queue')
      .update({ status: 'rejected' })
      .eq('id', row.id);
    if (error) {
      toast.error(`Kunde inte avvisa: ${error.message}`);
    } else {
      toast.success('Produkt avvisad');
      handleRowRemove(row.id);
    }
  };

  // Rensa kö — delete all non-published rows for this user
  const handleClearQueue = async () => {
    if (!supabase || !user) return;
    setClearing(true);
    const { error } = await supabase
      .from('pipeline_queue')
      .delete()
      .eq('user_id', user.id)
      .in('status', [
        'pending_review',
        'needs_manual_input',
        'scraping_failed',
        'generation_failed',
        'bullets_failed',
        'save_failed',
        'publish_failed',
        'partial_published',
        'rejected',
      ]);
    setClearing(false);
    setClearModal(false);
    if (error) {
      toast.error(`Kunde inte rensa kö: ${error.message}`);
    } else {
      toast.success('Kön rensad');
      loadRows();
    }
  };

  const bulkPublish = async () => {
    const toPublish = filteredRows.filter(r =>
      selected[r.id] &&
      (r.status === 'pending_review' || r.status === 'publish_failed' || r.status === 'partial_published')
    );
    if (toPublish.length === 0) return;
    if (!user || !supabase) return;
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;
    if (!accessToken) { toast.error('Ingen aktiv session'); return; }

    setBulkPublishing(true);
    let successes = 0;
    let fails = 0;

    for (const row of toPublish) {
      try {
        const content = row.generated_content || {};
        const validMarkets = (row.markets || []).filter(m => content[m] && !content[m].error);
        if (validMarkets.length === 0) { fails++; continue; }
        const resp = await fetch(`${API_URL}/api/autopilot/publish`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: user.id,
            access_token: accessToken,
            queue_id: row.id,
            part_no: row.part_no,
            markets: validMarkets,
            generated_content: content,
          }),
        });
        if (!resp.ok) { fails++; continue; }
        let finalStatus = 'publish_failed';
        let publishedMarkets = [];
        await consumeSse(resp, (ev) => {
          if (ev.type === 'done') {
            finalStatus = ev.status;
            publishedMarkets = ev.published_markets || [];
          }
        });
        if (finalStatus === 'published') successes++;
        else if (finalStatus === 'partial_published') successes++;
        else fails++;
        handleRowUpdate({
          id: row.id, status: finalStatus, published_markets: publishedMarkets,
          published_at: publishedMarkets.length ? new Date().toISOString() : row.published_at,
        });
      } catch (_) { fails++; }
    }

    setBulkPublishing(false);
    setSelected({});
    if (fails === 0) toast.success(`${successes} produkt${successes !== 1 ? 'er' : ''} publicerad${successes !== 1 ? 'e' : ''}`);
    else toast.warning(`${successes} lyckades, ${fails} misslyckades`);
  };

  if (loading) {
    return (
      <div className="ap-queue-empty" data-testid="ap-queue-loading">
        <Loader2 size={20} className="ap-progress-spin" style={{ marginBottom: 8 }} />
        <div>Hämtar kö...</div>
      </div>
    );
  }

  return (
    <div data-testid="ap-queue-tab">
      <div className="ap-queue-toolbar">
        {FILTERS.map(([key, label]) => (
          <button
            key={key}
            className={`ap-filter-pill${activeFilter === key ? ' ap-filter-pill--on' : ''}`}
            onClick={() => { setActiveFilter(key); setExpandedId(null); setSelected({}); }}
            data-testid={`ap-queue-filter-${key}`}
          >
            {label}
            <span className="ap-filter-count">{counts[key] || 0}</span>
          </button>
        ))}
        <div className="ap-queue-spacer" />
        <button
          className="ap-queue-bulk-btn"
          onClick={bulkPublish}
          disabled={selectedIds.length === 0 || bulkPublishing}
          data-testid="ap-queue-bulk-publish"
        >
          {bulkPublishing
            ? <><Loader2 size={13} className="ap-progress-spin" /> Publicerar {selectedIds.length}...</>
            : <><Upload size={13} /> Publicera valda ({selectedIds.length})</>}
        </button>
        <button
          className="ap-queue-clear-btn"
          onClick={() => setClearModal(true)}
          disabled={(counts['pending'] || 0) + (counts['rejected'] || 0) === 0}
          title="Rensa kön — tar bort alla väntande och avvisade produkter"
          data-testid="ap-queue-clear"
        >
          <Trash2 size={13} /> Rensa kö
        </button>
        <button className="ap-queue-refresh" onClick={loadRows} title="Uppdatera" data-testid="ap-queue-refresh">
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Rensa kö — bekräftelsemodal */}
      {clearModal && (
        <div className="ap-modal-overlay" data-testid="ap-clear-modal">
          <div className="ap-modal">
            <div className="ap-modal-icon"><AlertTriangle size={22} /></div>
            <h3 className="ap-modal-title">Rensa kön?</h3>
            <p className="ap-modal-body">
              Detta tar bort <strong>{(counts['pending'] || 0) + (counts['failed'] || 0) + (counts['rejected'] || 0)} produkt{((counts['pending'] || 0) + (counts['failed'] || 0) + (counts['rejected'] || 0)) !== 1 ? 'er' : ''}</strong> med
              status Väntar, Misslyckade eller Avvisade från kön. Publicerade produkter påverkas inte.
            </p>
            <div className="ap-modal-actions">
              <button className="ap-btn-pill" onClick={() => setClearModal(false)} disabled={clearing}
                data-testid="ap-clear-cancel">
                Avbryt
              </button>
              <button className="ap-btn-pill ap-btn-pill--danger" onClick={handleClearQueue} disabled={clearing}
                data-testid="ap-clear-confirm">
                {clearing ? <><Loader2 size={12} className="ap-progress-spin" /> Rensar...</> : <><Trash2 size={12} /> Rensa kön</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {filteredRows.length === 0 ? (
        <div className="ap-queue-empty" data-testid="ap-queue-empty">
          Inga produkter i denna vy
        </div>
      ) : (
        <div className="ap-queue-table-wrap">
          <table className="ap-queue-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={selectablePublishable.length === 0}
                    data-testid="ap-queue-select-all"
                    aria-label="Markera alla"
                  />
                </th>
                <th style={{ width: 18 }}></th>
                <th>Produkt</th>
                <th>Code</th>
                <th>Marknader</th>
                <th>Status</th>
                <th>Skapad</th>
                <th style={{ width: 180, textAlign: 'right' }}>Åtgärder</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map(r => {
                const expanded = expandedId === r.id;
                const canSelect = r.status === 'pending_review' || r.status === 'publish_failed' || r.status === 'partial_published';
                return (
                  <React.Fragment key={r.id}>
                    <tr
                      className={expanded ? 'ap-queue-row--expanded' : ''}
                      onClick={() => setExpandedId(expanded ? null : r.id)}
                      data-testid={`ap-queue-row-${r.id}`}
                    >
                      <td onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={!!selected[r.id]}
                          disabled={!canSelect}
                          onChange={() => setSelected(prev => ({ ...prev, [r.id]: !prev[r.id] }))}
                          data-testid={`ap-queue-check-${r.id}`}
                        />
                      </td>
                      <td>{expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                      <td style={{ fontWeight: 600 }}>{r.product_name || '—'}</td>
                      <td><code style={{ fontSize: 11 }}>{r.part_no}</code></td>
                      <td>
                        <div className="ap-mkt-chips">
                          {(r.markets || []).map(m => {
                            const isPub = (r.published_markets || []).includes(m);
                            const hasErr = r.publish_errors && r.publish_errors[m];
                            return (
                              <span
                                key={m}
                                className={`ap-mkt-chip${isPub ? ' ap-mkt-chip--ok' : hasErr ? ' ap-mkt-chip--fail' : ''}`}
                                title={hasErr || ''}
                              >
                                <MarketFlag market={m} /> {m}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                      <td>
                        <span className={`ap-queue-status ap-queue-status--${r.status}`}>
                          {STATUS_LABELS[r.status] || r.status}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: '#64748b' }}>
                        <Clock size={11} style={{ verticalAlign: '-1px', marginRight: 4 }} />
                        {formatDate(r.created_at)}
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <div className="ap-queue-row-actions">
                          {canSelect && (
                            <button
                              className="ap-btn-pill ap-btn-pill--primary"
                              onClick={() => setExpandedId(r.id)}
                              data-testid={`ap-queue-open-${r.id}`}
                            >
                              <Upload size={11} /> Publicera
                            </button>
                          )}
                          {canSelect && (
                            <button
                              className="ap-btn-pill ap-btn-pill--danger"
                              onClick={(e) => handleQuickReject(e, r)}
                              data-testid={`ap-queue-quick-reject-${r.id}`}
                              title="Avvisa produkt"
                            >
                              <XCircle size={11} /> Avvisa
                            </button>
                          )}
                          {r.status === 'published' && (
                            <span className="ap-btn-pill" style={{ cursor: 'default' }}>
                              <CheckCircle2 size={11} /> Publicerad
                            </span>
                          )}
                          {r.status === 'rejected' && (
                            <span className="ap-btn-pill" style={{ cursor: 'default' }}>
                              <XCircle size={11} /> Avvisad
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="ap-queue-detail">
                        <td colSpan={8}>
                          <QueueRowDetail
                            row={r}
                            onSaved={handleRowUpdate}
                            onRemoved={handleRowRemove}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
